/**
 * @file SignedInDevices.tsx
 * @brief Profile → Devices: the devices signed in to this account, with
 *        "Sign out" on each (one account = one person, launch pricing,
 *        DES 18-10-2026).
 *
 * @details
 * At most 3 signed-in devices; a 4th sign-in signs out the oldest (server).
 * Proposed contract (sent to the backend): GET /api/auth/devices →
 * { devices: [{ id, label, platform, lastSeenAt, createdAt, current }], limit },
 * DELETE /api/auth/devices/:id. Signing out this device logs out here.
 * `useSignedInDevices` resolves `null` when the server has no such route
 * (404 on today's backend), and the Profile page then hides the tab.
 *
 * @author DES
 * @date 18-10-2026
 */

import { LoaderCircle, Monitor, Smartphone } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { ApiClientError, apiDelete, apiGet } from "@/shared/api/client";
import { authEndpoints } from "@/shared/api/endpoints";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import { formatRelative } from "@/shared/utils/formatters";

const DeviceSchema = z
  .object({
    id: z.string(),
    label: z.string().nullable().optional(),
    platform: z.string().nullable().optional(),
    lastSeenAt: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    current: z.boolean().optional(),
  })
  .passthrough();
const DevicesSchema = z.object({ devices: z.array(DeviceSchema).default([]), limit: z.number().optional() });
type Devices = z.infer<typeof DevicesSchema>;

const KEY = ["profile", "devices"] as const;

/** The device list, or null when this backend has no devices route yet. */
export function useSignedInDevices() {
  return useQuery<Devices | null>({
    queryKey: KEY,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return DevicesSchema.parse(await apiGet<unknown>(authEndpoints.devices));
      } catch (err) {
        if (err instanceof ApiClientError && (err.status === 404 || err.status === 405)) return null;
        throw err;
      }
    },
  });
}

const PLATFORM: Record<string, string> = { web: "Web", desktop: "Mac app", ios: "iPhone", android: "Android" };

export function SignedInDevices() {
  const list = useSignedInDevices();
  const qc = useQueryClient();
  const { logout } = useAuth();
  const signOut = useMutation({
    mutationFn: (id: string) => apiDelete<unknown>(authEndpoints.deviceById(id)),
    onSettled: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const devices = list.data?.devices ?? [];
  const limit = list.data?.limit ?? 3;

  const onSignOut = async (d: (typeof devices)[number]) => {
    try {
      await signOut.mutateAsync(d.id);
      if (d.current) {
        await logout();
        return;
      }
      toast({ title: "Signed out", description: `${d.label || PLATFORM[d.platform ?? ""] || "That device"} is signed out.` });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn't sign out that device",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <div className="border-t border-border/60">
      <GridBand className="text-sm font-medium">
        Signed-in devices
        <span className="ml-1 font-normal text-muted-foreground">
          · Up to {limit}. Signing in on another device signs out the oldest.
        </span>
      </GridBand>
      {list.isLoading ? (
        <div className="px-4 py-3.5 text-sm text-muted-foreground md:px-5">Checking…</div>
      ) : list.isError ? (
        <div className="px-4 py-3.5 text-sm text-muted-foreground md:px-5">Could not load your devices.</div>
      ) : devices.length === 0 ? (
        // Devices register when they sign in; sessions from before the
        // device list existed show up after their next sign-in.
        <div className="px-4 py-3.5 text-sm text-muted-foreground md:px-5">
          No devices listed yet. A device appears here the next time it signs in.
        </div>
      ) : null}
      <ul>
        {devices.map((d) => {
          const busy = signOut.isPending && signOut.variables === d.id;
          const mobile = d.platform === "ios" || d.platform === "android";
          const Icon = mobile ? Smartphone : Monitor;
          return (
            <li key={d.id} className="flex items-center gap-3 border-b border-border/60 px-4 py-3.5 last:border-b-0 md:px-5">
              <Icon className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {d.label || PLATFORM[d.platform ?? ""] || "Device"}
                  {d.current ? <span className="ml-1.5 font-normal text-muted-foreground">This device</span> : null}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {PLATFORM[d.platform ?? ""] ?? ""}
                  {d.lastSeenAt ? ` · active ${formatRelative(d.lastSeenAt)}` : ""}
                </span>
              </span>
              <Button variant="ghost" size="sm" className="text-muted-foreground" disabled={busy} onClick={() => void onSignOut(d)}>
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Sign out"}
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
